import React, { useMemo, useState } from 'react';
import type { StaffMember, Shift, Schedule, Department, ScheduleEntry, AttendanceRecord, AttendanceRequest } from '../../types';
import Modal from '../ui/Modal';
import { Icons } from '../ui/Icons';
import { toast } from 'react-hot-toast';

interface DailyShiftDetailsModalProps {
    date: string;
    currentStaffMember: StaffMember;
    allStaff: StaffMember[];
    schedule: Schedule;
    shifts: Shift[];
    departments: Department[];
    attendanceRecords: AttendanceRecord[];
    attendanceRequests: AttendanceRequest[];
    onUpdateScheduleEntry?: (staffId: string, date: string, updates: { actualStartTime: string, actualEndTime: string, reason: string }) => void;
    onClose: () => void;
}

const getInitials = (name: string) => {
    if (!name) return '';
    const names = name.split(' ');
    if (names.length > 1 && names[names.length - 1]) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};

const toMinutes = (timeString: string): number => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
};

// FIX: Explicitly type StaffListItem as a React.FC to resolve errors with the 'key' prop.
const StaffListItem: React.FC<{ item: { staff: StaffMember, shift: Shift | null, department: Department | null } }> = ({ item }) => (
    <li className="flex items-center gap-3 py-2">
        <div
            className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold text-xs"
            style={{ backgroundColor: item.staff.color }}
        >
            {getInitials(item.staff.name)}
        </div>
        <div className="flex-grow">
            <p className="font-semibold text-gray-800 dark:text-gray-200">{item.staff.name}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
                {item.department ? item.department.name : 'No Department'}
            </p>
        </div>
        {item.shift && (
            <div className="text-right text-xs">
                <p className="font-medium text-gray-700 dark:text-gray-300">{item.shift.name}</p>
                <p className="text-gray-500 dark:text-gray-400">{item.shift.startTime} - {item.shift.endTime}</p>
            </div>
        )}
    </li>
);


const DailyShiftDetailsModal: React.FC<DailyShiftDetailsModalProps> = ({ date, currentStaffMember, allStaff, schedule, shifts, departments, attendanceRecords, attendanceRequests, onUpdateScheduleEntry, onClose }) => {
    
    const [isUpdatingTime, setIsUpdatingTime] = useState(false);
    const [actualStartTime, setActualStartTime] = useState('');
    const [actualEndTime, setActualEndTime] = useState('');
    const [reason, setReason] = useState('');

    const shiftsMap = useMemo(() => new Map<string, Shift>(shifts.map(s => [s.id, s])), [shifts]);
    const departmentsMap = useMemo(() => new Map<string, Department>(departments.map(d => [d.id, d])), [departments]);

    const formattedDate = new Date(date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });

    const myEntry = schedule[currentStaffMember.id]?.[date];
    const myShift = myEntry?.shiftId ? shiftsMap.get(myEntry.shiftId) : null;
    
    const myAttendance = useMemo(() => {
        return attendanceRecords.find(ar => ar.employeeId === currentStaffMember.id && ar.date === date && ar.shiftId === myEntry?.shiftId);
    }, [attendanceRecords, currentStaffMember.id, date, myEntry?.shiftId]);

    const myPendingRequest = useMemo(() => {
        return attendanceRequests.find(r => r.employeeId === currentStaffMember.id && r.date === date && r.shiftId === myEntry?.shiftId && r.status === 'PENDING');
    }, [attendanceRequests, currentStaffMember.id, date, myEntry?.shiftId]);

    // Initialize actual times from attendance or shift
    React.useEffect(() => {
        if (myShift) {
            setActualStartTime(myPendingRequest?.requestedStartTime || myAttendance?.actualStartTime || myShift.startTime);
            setActualEndTime(myPendingRequest?.requestedEndTime || myAttendance?.actualEndTime || myShift.endTime);
            setReason(myPendingRequest?.staffNote || '');
        }
    }, [myShift, myAttendance, myPendingRequest]);

    const hasShiftStarted = useMemo(() => {
        if (!myShift) return false;
        const [year, month, day] = date.split('-').map(Number);
        const [hours, minutes] = myShift.startTime.split(':').map(Number);
        const shiftStart = new Date(year, month - 1, day, hours, minutes);
        return new Date() >= shiftStart;
    }, [date, myShift]);

    const isPastUpdateLimit = useMemo(() => {
        if (!myShift) return false;
        const [year, month, day] = date.split('-').map(Number);
        const [startHours, startMinutes] = myShift.startTime.split(':').map(Number);
        const [endHours, endMinutes] = myShift.endTime.split(':').map(Number);
        
        let shiftEnd = new Date(year, month - 1, day, endHours, endMinutes);
        
        // If end time is before start time, it means shift ends the next day
        if (toMinutes(myShift.endTime) < toMinutes(myShift.startTime)) {
            shiftEnd.setDate(shiftEnd.getDate() + 1);
        }
        
        const limitTime = new Date(shiftEnd.getTime() + 12 * 60 * 60 * 1000);
        return new Date() > limitTime;
    }, [date, myShift]);

    const handleSaveTime = () => {
        if (!myShift) return;
        
        if (isPastUpdateLimit) {
            toast.error("You can only update your hours within 12 hours of the shift end time.");
            return;
        }
        
        const isMatch = actualStartTime === myShift.startTime && actualEndTime === myShift.endTime;
        if (!isMatch && !reason.trim()) {
            toast.error("Please provide a reason for the time adjustment.");
            return;
        }

        if (onUpdateScheduleEntry) {
            onUpdateScheduleEntry(currentStaffMember.id, date, {
                actualStartTime,
                actualEndTime,
                reason
            });
            setIsUpdatingTime(false);
        }
    };

    const myShiftEndTime = myShift ? toMinutes(myShift.endTime) : -1;

    const myDeptId = myEntry?.departmentId || (currentStaffMember.departmentIds && currentStaffMember.departmentIds.length > 0 ? currentStaffMember.departmentIds[0] : null);
    const myDept = myDeptId ? departmentsMap.get(myDeptId) : null;

    const colleaguesOnShift = useMemo(() => {
        return allStaff
            .filter(staff => staff.id !== currentStaffMember.id && schedule[staff.id]?.[date]?.status === 'ASSIGNED')
            .map(staff => {
                const entry = schedule[staff.id][date];
                const shift = entry.shiftId ? shiftsMap.get(entry.shiftId) : null;
                const depId = entry.departmentId || (staff.departmentIds && staff.departmentIds.length > 0 ? staff.departmentIds[0] : null);
                const department = depId ? departmentsMap.get(depId) : null;
                return { staff, shift, department };
            })
            .filter(item => !!item.shift);
    }, [allStaff, schedule, date, currentStaffMember.id, shiftsMap, departmentsMap]);

    const reliefTeam = useMemo(() => {
        if (!myShift) return [];
        const myPrimaryDepId = currentStaffMember.departmentIds?.[0];
        return colleaguesOnShift
            .filter(colleague => 
                colleague.staff.departmentIds?.includes(myPrimaryDepId) &&
                colleague.shift && toMinutes(colleague.shift.startTime) >= myShiftEndTime
            )
            .sort((a, b) => toMinutes(a.shift!.startTime) - toMinutes(b.shift!.startTime));
    }, [myShift, colleaguesOnShift, currentStaffMember.departmentIds, myShiftEndTime]);

    return (
        <Modal title={`Shift Details for ${formattedDate}`} onClose={onClose}>
            <div className="p-6 space-y-6">
                <div>
                    <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Your Shift</h3>
                    {myShift ? (
                        <div className="p-3 rounded-lg text-white" style={{ backgroundColor: myShift.color }}>
                            <p className="font-bold text-lg">{myShift.name}</p>
                            <div className="flex items-center justify-between mt-1">
                                <p className="text-sm opacity-90">{myShift.startTime} - {myShift.endTime}</p>
                                {myDept && (
                                    <span className="text-xs font-bold px-2 py-0.5 rounded bg-black/20 text-white uppercase tracking-wider">
                                        {myDept.name}
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : (
                        <p className="p-3 bg-gray-100 dark:bg-slate-700 rounded-lg text-sm text-gray-600 dark:text-gray-300">You are not scheduled for a shift on this day.</p>
                    )}
                </div>

                {myShift && (
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-xl border border-indigo-100 dark:border-indigo-900/30">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-bold text-indigo-900 dark:text-indigo-300 uppercase tracking-wider">Actual Shift Times</h3>
                            {!isUpdatingTime && hasShiftStarted && !isPastUpdateLimit && (
                            <div className="flex items-center gap-3">
                                {myPendingRequest ? (
                                     <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 px-3 py-1.5 rounded-lg border border-indigo-100 dark:border-indigo-900/30">
                                        <Icons.Clock className="w-3 h-3 animate-pulse" />
                                        <p className="text-[10px] font-bold uppercase tracking-wider">Approval Pending</p>
                                     </div>
                                ) : (
                                    <>
                                        <button 
                                            onClick={() => {
                                                if (myShift) {
                                                    setActualStartTime(myShift.startTime);
                                                    setActualEndTime(myShift.endTime);
                                                    setIsUpdatingTime(true);
                                                    toast.success("Times set to scheduled shift times");
                                                }
                                            }}
                                            className="text-xs font-bold text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1"
                                        >
                                            <Icons.Clock className="w-3 h-3" />
                                            Same Time
                                        </button>
                                        <button 
                                            onClick={() => setIsUpdatingTime(true)}
                                            className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                                        >
                                            <Icons.Edit className="w-3 h-3" />
                                            Update Times
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    {isUpdatingTime ? (
                        <div className="space-y-4">
                            <button 
                                onClick={() => {
                                    if (myShift) {
                                        setActualStartTime(myShift.startTime);
                                        setActualEndTime(myShift.endTime);
                                        toast.success("Times set to scheduled shift times");
                                    }
                                }}
                                className="w-full py-1.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 text-[10px] font-bold rounded-lg border border-emerald-100 dark:border-emerald-900/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors flex items-center justify-center gap-1.5 uppercase tracking-wider"
                            >
                                <Icons.Clock className="w-3 h-3" />
                                Use Scheduled Times (Same Time)
                            </button>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Start Time</label>
                                    <input 
                                        type="time"
                                        value={actualStartTime}
                                        onChange={(e) => setActualStartTime(e.target.value)}
                                        className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">End Time</label>
                                    <input 
                                        type="time"
                                        value={actualEndTime}
                                        onChange={(e) => setActualEndTime(e.target.value)}
                                        className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                    />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Reason (Optional)</label>
                                <input 
                                    type="text"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="e.g. Stayed late for cleanup"
                                    className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                />
                            </div>
                            <div className="flex gap-2">
                                <button 
                                    onClick={handleSaveTime}
                                    className="flex-grow py-2 bg-indigo-600 text-white text-sm font-bold rounded-lg hover:bg-indigo-700 transition-colors"
                                >
                                    Save Changes
                                </button>
                                <button 
                                    onClick={() => setIsUpdatingTime(false)}
                                    className="px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-200 text-sm font-bold rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 transition-colors"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between">
                            <div className="flex gap-4">
                                <div>
                                    <p className="text-[10px] text-gray-400 uppercase font-bold">
                                        {myPendingRequest ? 'Requested Start' : 'Actual Start'}
                                    </p>
                                    <p className="text-lg font-bold text-gray-800 dark:text-gray-200">
                                        {myPendingRequest ? myPendingRequest.requestedStartTime : (myAttendance?.actualStartTime || '--:--')}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] text-gray-400 uppercase font-bold">
                                         {myPendingRequest ? 'Requested End' : 'Actual End'}
                                    </p>
                                    <p className="text-lg font-bold text-gray-800 dark:text-gray-200">
                                        {myPendingRequest ? myPendingRequest.requestedEndTime : (myAttendance?.actualEndTime || '--:--')}
                                    </p>
                                </div>
                            </div>
                                {!hasShiftStarted && (
                                    <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 rounded-lg border border-amber-100 dark:border-amber-900/30">
                                        <Icons.AlertTriangle className="w-4 h-4" />
                                        <p className="text-xs font-medium">Updates available once shift starts</p>
                                    </div>
                                )}
                                {hasShiftStarted && isPastUpdateLimit && (
                                    <div className="flex items-center gap-2 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg border border-red-100 dark:border-red-900/30">
                                        <Icons.AlertTriangle className="w-4 h-4" />
                                        <p className="text-xs font-medium">Update period expired (12h limit)</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                <div>
                    <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Working With You</h3>
                    <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 max-h-48 overflow-y-auto">
                        {colleaguesOnShift.length > 0 ? (
                            <ul className="divide-y divide-gray-200 dark:divide-slate-600">
                                {(colleaguesOnShift || []).map(item => <StaffListItem key={item.staff.id} item={item} />)}
                            </ul>
                        ) : (
                            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No other staff members are scheduled today.</p>
                        )}
                    </div>
                </div>

                {myShift && (
                     <div>
                        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Relief Team (Your Department)</h3>
                        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 max-h-48 overflow-y-auto">
                            {(reliefTeam?.length || 0) > 0 ? (
                                <ul className="divide-y divide-gray-200 dark:divide-slate-600">
                                    {(reliefTeam || []).map(item => <StaffListItem key={item.staff.id} item={item} />)}
                                </ul>
                            ) : (
                                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No relief staff from your department are scheduled after your shift.</p>
                            )}
                        </div>
                    </div>
                )}
            </div>
             <div className="bg-gray-50 dark:bg-slate-900/50 px-4 py-3 sm:px-6 flex justify-end border-t dark:border-slate-700">
                <button
                    type="button"
                    onClick={onClose}
                    className="w-full sm:w-auto inline-flex justify-center rounded-md border border-gray-300 dark:border-slate-600 shadow-sm px-4 py-2 bg-white dark:bg-slate-700 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:text-sm"
                >
                    Close
                </button>
            </div>
        </Modal>
    );
};

export default DailyShiftDetailsModal;